import { useCallback, useEffect, useMemo, useState } from 'react';
import { getLocations } from '@rideiq/api/services/Locations';
import * as Constants from '@rideiq/constants';
import type { ILocationQueryParams, LocationsQueryParams } from '@rideiq/locations/types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

export const LOCATIONS_QUERY_KEY = 'locations';

interface LocationsQueryOptions extends Pick<LocationsQueryParams, 'include' | 'locationType'> {
  paginationMode?: 'infinite' | 'paged';
}

export const useLocationsQuery = (
  initialPerPage: number = Constants.ITEM_LIMIT_PER_PAGE,
  { paginationMode, ...initialFilters }: LocationsQueryOptions = {}
) => {
  const isPaged = paginationMode !== 'infinite';

  const [queryParams, setQueryParams] = useState<LocationsQueryParams>({
    page: 1,
    perPage: initialPerPage,
    query: undefined,
    ...initialFilters,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [globalTotalCount, setGlobalTotalCount] = useState(0);

  const apiParams = useMemo(
    () => ({
      perPage: queryParams.perPage,
      page: queryParams.page,
      q: queryParams.query,
      ...(queryParams.include ? { include: queryParams.include } : {}),
      ...(queryParams.locationType ? { locationType: queryParams.locationType } : {}),
      ...(queryParams.sortBy
        ? {
            sortField: queryParams.sortBy === 'locationType' ? 'location_type' : queryParams.sortBy,
            sortDir: queryParams.sortDir ?? 'asc',
          }
        : {}),
    }),
    [queryParams]
  );

  // Scrolling pickers always use infinite pagination, independent of the table sorting flag.
  const {
    data: infiniteData,
    isFetching: infiniteFetching,
    isLoadingError: infiniteLoadingError,
    fetchNextPage,
    refetch: refetchInfinite,
  } = useInfiniteQuery({
    queryKey: [LOCATIONS_QUERY_KEY, 'infinite', apiParams],
    queryFn: async ({ pageParam, signal }) => {
      const response = await getLocations({ ...apiParams, page: pageParam as number }, signal);

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load locations');
      }

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage.locations?.length ?? 0) === apiParams.perPage ? allPages.length + 1 : undefined,
    enabled: !isPaged,
  });

  // The Locations table uses page-based results; scrolling pickers opt into infinite pagination.
  const {
    data: pagedData,
    isFetching: pagedFetching,
    isLoadingError: pagedLoadingError,
    refetch: refetchPaged,
  } = useQuery({
    queryKey: [LOCATIONS_QUERY_KEY, 'paged', apiParams],
    queryFn: async ({ signal }) => {
      const response = await getLocations(apiParams, signal);

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load locations');
      }

      return response.data;
    },
    enabled: isPaged,
  });

  // Derive totalCount/globalTotalCount from query data rather than as a queryFn side effect
  useEffect(() => {
    const meta = isPaged ? pagedData?.meta : infiniteData?.pages.at(-1)?.meta;

    if (meta?.totalCount !== undefined) {
      setTotalCount(meta.totalCount);
      if (!apiParams.locationType) {
        setGlobalTotalCount(meta.totalCount);
      }
    } else if (!apiParams.locationType) {
      // Fallback when API omits meta.totalCount: mirror legacy behaviour of using accumulated count
      const accumulatedCount = isPaged
        ? pagedData?.locations?.length ?? 0
        : infiniteData?.pages.flatMap((p) => p.locations ?? []).length ?? 0;

      if (accumulatedCount > 0) {
        setGlobalTotalCount(accumulatedCount);
      }
    }
  }, [pagedData, infiniteData, apiParams.locationType, isPaged]);

  const locations = useMemo(
    () => (isPaged ? pagedData?.locations ?? [] : infiniteData?.pages.flatMap((p) => p.locations ?? []) ?? []),
    [isPaged, pagedData, infiniteData]
  );

  const isLoading = isPaged ? pagedFetching : infiniteFetching;

  const handlePagination = useCallback(() => {
    if (!isPaged) {
      fetchNextPage();
    } else {
      setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  }, [isPaged, fetchNextPage]);

  const handlePageChange = useCallback(
    ({ page, perPage }: { page: number; perPage: number }, isPaginated?: boolean) => {
      if (isPaginated) {
        window.scrollTo(0, 0);
      }
      setQueryParams((prev) => ({ ...prev, page, perPage }));
    },
    []
  );

  const handleLocationType = useCallback((locationType: string | null) => {
    setQueryParams((prev) => ({
      ...prev,
      locationType: locationType ?? undefined,
      page: 1,
    }));
  }, []);

  const handleSort = useCallback((column: string) => {
    setQueryParams((prev) => {
      if (prev.sortBy === column) {
        if (prev.sortDir === 'asc') {
          return { ...prev, sortDir: 'desc', page: 1 };
        }

        return { ...prev, sortBy: null, sortDir: null, page: 1 };
      }

      return { ...prev, sortBy: column, sortDir: 'asc', page: 1 };
    });
  }, []);

  const clearSort = useCallback(() => {
    setQueryParams((prev) => ({ ...prev, sortBy: null, sortDir: null, page: 1 }));
  }, []);

  const initSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setQueryParams((prev) => ({ ...prev, sortBy: column, sortDir: direction, page: 1 }));
  }, []);

  function handleQuery(query: string | null, { force = false } = {}) {
    const normalizedQuery = query ?? '';

    if (normalizedQuery.length > 0 && normalizedQuery.length < Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      return;
    }
    setQueryParams((prev) => {
      if (!force && normalizedQuery === (prev.query ?? '')) {
        return prev;
      }

      return { ...prev, query: normalizedQuery.length > 0 ? normalizedQuery : undefined, page: 1 };
    });
  }

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, Constants.DEBOUNCE_TIME), []);

  useEffect(() => () => debounceHandleQuery.cancel(), [debounceHandleQuery]);

  const fetchLocations = useCallback(
    (params?: ILocationQueryParams) => {
      if (params) {
        setQueryParams((prev) => ({ ...prev, ...params }));
      } else if (isPaged) {
        refetchPaged();
      } else {
        refetchInfinite();
      }
    },
    [isPaged, refetchPaged, refetchInfinite]
  );

  return {
    locations,
    queryParams,
    setQueryParams,
    isLoading,
    isLoadingError: isPaged ? pagedLoadingError : infiniteLoadingError,
    totalCount,
    globalTotalCount,
    fetchLocations,
    handlePagination,
    handlePageChange,
    handleLocationType,
    handleSort,
    clearSort,
    initSort,
    debounceHandleQuery,
  };
};
